import { PRIMARY_DATE_SORT } from './cards';

/**
 * What a table's column header can do (KPR-1638, KPE-051 ruling i04), as data:
 * which field a column sorts by, why one cannot, and where a click on the
 * header's label takes the order next. Pure, so the header component, the
 * hook that draws it and the tests all read one rule.
 *
 * A column is named by its antd `key` (components/items/ItemsTable): `el:<name>`
 * for a card's element, `sys:<field>` for one of the item's own fields in a
 * card's table, and the mixed list's own keys (`title`, `cardType`,
 * `primaryDate`, `updated`).
 */

const SYSTEM_DATE_SORTS = {
	'sys:primaryDate': PRIMARY_DATE_SORT,
	primaryDate: PRIMARY_DATE_SORT,
	'sys:updatedAt': 'updatedAt',
	updated: 'updatedAt',
	'sys:createdAt': 'createdAt',
	created: 'createdAt',
};

// Why a column that keepr cannot order says so, in the menu.
const NO_SORT_REASON = {
	title: 'A title is made from the card’s fields. Sort by one of them.',
	'sys:title': 'A title is made from the card’s fields. Sort by one of them.',
	'sys:sourceCreatedAt': 'The source system’s date is shown for reference and has no order here.',
	'sys:tags': 'Tags have no order to sort by.',
	'sys:owner': 'Who made an item has no order to sort by.',
	cardType: 'Items of different cards have no shared order by type.',
	'sys:collection': 'Sub-collections have no order to sort by.',
};

const FILE_REASON = 'Files have no order to sort by.';
const NOT_OFFERED_REASON = 'This field cannot be sorted.';

/**
 * How one column sorts: `{ field, reason }` where exactly one is set.
 * `defsByName` is the card's elements by name and `offered` the set of sort
 * fields the list offers now (useListSort `sortOptions`); a field the list
 * does not offer (an element this person may not see, one the card dropped) is
 * not sortable here either, since it would be reset to the default.
 */
export function columnSortOf(key, { defsByName = null, offered = null } = {}) {
	const reasonFor = NO_SORT_REASON[key];
	if (reasonFor) return { field: null, reason: reasonFor };
	const system = SYSTEM_DATE_SORTS[key];
	if (system) return offered && !offered.has(system) ? { field: null, reason: NOT_OFFERED_REASON } : { field: system, reason: null };
	if (typeof key === 'string' && key.startsWith('el:')) {
		const name = key.slice(3);
		const def = defsByName?.get(name);
		if (def?.dataType === 'file') return { field: null, reason: FILE_REASON };
		if (!def || (offered && !offered.has(name))) return { field: null, reason: NOT_OFFERED_REASON };
		return { field: name, reason: null };
	}
	return { field: null, reason: NOT_OFFERED_REASON };
}

/**
 * A column's sort state: 'asc' or 'desc' when it is the list's first sort
 * level, else null (a "then by" level is not drawn on the header).
 */
export const columnSortState = (field, sort) => (field && sort?.field === field ? (sort.direction === 'asc' ? 'asc' : 'desc') : null);

/**
 * Where a click on the header's label goes: ascending, then descending, then
 * back to the default order (a table layout's own, else the primary date).
 * Returns the next level 1 as `{ field, direction }`, or `{ field: null }` for
 * "back to the default".
 *
 * Descending clears, except where clearing would change nothing, which would
 * leave the cycle stuck on that column: the order in force is the unchosen
 * default already (`sort.source` says so: a layout's or the platform's), or it
 * is the primary date newest first, which is what clearing gives.
 */
export function nextHeaderSort(field, sort) {
	const state = columnSortState(field, sort);
	if (state === null) return { field, direction: 'asc' };
	if (state === 'asc') return { field, direction: 'desc' };
	const clearsToNothing = Boolean(sort?.source) || field === PRIMARY_DATE_SORT;
	return clearsToNothing ? { field, direction: 'asc' } : { field: null };
}

/** Whether "Clear sort" does anything for this column: it is the first level and chosen. */
export function canClearSort(field, sort) {
	return columnSortState(field, sort) !== null && !sort?.source && !(field === PRIMARY_DATE_SORT && sort.direction !== 'asc');
}

/** The aria-sort value of a header: 'ascending', 'descending', 'none', or null for a column that cannot sort. */
export function ariaSortOf(field, sort) {
	if (!field) return null;
	const state = columnSortState(field, sort);
	return state === 'asc' ? 'ascending' : state === 'desc' ? 'descending' : 'none';
}

/** A column heading as text, whatever its title is (a string, or the primary date's span with a tooltip). */
export function headerLabelOf(title) {
	if (typeof title === 'string') return title;
	const children = title?.props?.children;
	return typeof children === 'string' ? children : '';
}
